import { type JSX } from 'react'
import { useLocation } from 'react-router'
import { X, Plus, XCircle } from 'lucide-react'
import Tooltip from '../ui/Tooltip.js'

export interface Tab {
  id: string
  path: string
  label: string
  icon?: React.ReactNode
  /** Path that was active when this tab was opened. Closing the tab returns
   *  there, so a conversation opened from the inbox lands back on the inbox
   *  rather than on whichever tab happens to sit last in the bar. */
  openedFrom?: string
}

interface AgentTabsProps {
  tabs: Tab[]
  onCloseTab: (id: string) => void
  onCloseAllTabs: () => void
  onNavigate: (path: string) => void
}

// Chrome-style "new tab" launcher route. The "+" button opens it instead of
// the old inline dropdown; the page itself (NewTabPage) holds the global
// search + page shortcuts.
const NEW_TAB_PATH = '/agent/new-tab'

export default function AgentTabs({ tabs, onCloseTab, onCloseAllTabs, onNavigate }: AgentTabsProps): JSX.Element {
  const location = useLocation()

  const isActive = (path: string): boolean => {
    if (path === '/agent') {
      return location.pathname === '/agent' || location.pathname === '/agent/'
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  // If no tabs, render empty placeholder bar so layout is stable
  if (tabs.length === 0) {
    return (
      <div className="flex items-center border-b border-slate-200 bg-slate-50 h-10">
        <div className="flex-1" />
      </div>
    )
  }

  return (
    <div className="flex items-center border-b border-slate-200 bg-slate-50">
      {/* Scrollable region — only the tabs themselves scroll; the new-tab
          and close-all controls below stay pinned outside this area so
          they're always reachable without scrolling to the end. */}
      <div className="flex flex-1 items-center overflow-x-auto scrollbar-thin">
        {tabs.map((tab) => {
          const active = isActive(tab.path)
          return (
            <div
              key={tab.id}
              className={`group flex items-center gap-2 px-3 py-2 min-w-[140px] max-w-[200px] transition-all cursor-pointer flex-shrink-0 ${
                active
                  ? 'bg-white border-t-2 border-t-[#3366FF] shadow-sm'
                  : 'bg-slate-100 hover:bg-slate-200'
              }`}
              onClick={() => onNavigate(tab.path)}
            >
              {tab.icon && (
                <span className={`flex-shrink-0 ${active ? 'text-[#3366FF]' : 'text-slate-400'}`}>
                  {tab.icon}
                </span>
              )}
              <span className="flex-1 text-left truncate text-[13px] font-medium text-slate-700">
                {tab.label}
              </span>
              <Tooltip content="Close tab">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onCloseTab(tab.id)
                  }}
                  aria-label="Close tab"
                  className={`flex-shrink-0 rounded-[3px] p-0.5 transition-all ${
                    active
                      ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                      : 'text-slate-400 opacity-0 group-hover:opacity-100 hover:text-slate-600 hover:bg-slate-300'
                  }`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </div>
          )
        })}

        {/* New tab button — opens the Chrome-style launcher page. Kept
            inside the scroll region, right after the last tab, matching
            the browser convention of a "+" immediately following tabs. */}
        <div className="flex-shrink-0 px-1">
          <Tooltip content="Open new tab">
            <button
              onClick={() => onNavigate(NEW_TAB_PATH)}
              aria-label="Open new tab"
              className="flex items-center justify-center h-9 w-9 rounded-[3px] text-slate-400 hover:text-[#3366FF] hover:bg-slate-100 transition-all"
            >
              <Plus className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Close all tabs — pinned outside the scrollable region so it's
          always visible at the end of the bar, even when there are enough
          tabs to overflow. Only meaningful once more than one tab is open
          (My Dashboard always survives this action). */}
      {tabs.length > 1 && (
        <div className="flex-shrink-0 border-l border-slate-200 bg-slate-50 px-1">
          <Tooltip content="Close all tabs">
            <button
              onClick={onCloseAllTabs}
              aria-label="Close all tabs"
              className="flex items-center justify-center h-9 w-9 rounded-[3px] text-slate-400 hover:text-red-500 hover:bg-slate-100 transition-all"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  )
}
